/**
 * S-01 Landing / Login (FOUNDATION — the one screen the foundation builds
 * FULLY: this IS the brand, front and center — PRD §2 / screens.md S-01).
 *
 * v6 "WARM FRONT DOOR" (owner reframe on the v5 "PIXEL SWEETHEART" look):
 * fun, light, no-pressure. TICKER the pixel mascot is the SINGLE hero heart
 * (the nav is a plain HUMBLE wordmark now — no second stacked heart), the
 * chunky tagline underlines "wrong" in a strawberry swash, and the three
 * panels sell the warm basics — the twist mechanics stay a playful surprise
 * inside the app ("You'll see"), never the headline. The candy palette,
 * pixel hearts and gummy buttons stay. Same data flow + selectors as v1-v5
 * (.landing__tagline, .landing__panels .panel ×3 with "LITTLE SURPRISES" on
 * the third, "Create account", #landing-email / #landing-password, all
 * links).
 */
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { copy, resolveCopy } from "@humble/shared";
import {
  AmbientBackground,
  Button,
  Card,
  GrainOverlay,
  Mascot,
  PixelHeart,
  PixelHeartCracked,
  PixelSparkle,
  rise,
  springs,
  stagger,
  toast,
} from "../../design/index.js";
import { api, getDeviceFp, isApiError } from "../../lib/api.js";
import { sessionStore, setSession, useStore } from "../../lib/store.js";
import "./landing.css";

function routeForLifecycle(lifecycle: string | null | undefined): string {
  switch (lifecycle) {
    case "registered_unverified":
      return "/verify-email";
    case "onboarding":
      return "/onboard/identity";
    case "deletion_grace":
      return "/grace";
    case "banned":
      return "/banned";
    default:
      return "/deck";
  }
}

/** The living word — one playful accent inside the friendly chunky line:
 *  "wrong" in a strawberry gradient with a hand-drawn underline swash
 *  drawing in. One warm wink IS the brand voice. */
function Wrong() {
  return (
    <em className="landing__accent">
      wrong
      <svg
        className="landing__accent-stroke"
        viewBox="0 0 120 14"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <motion.path
          d="M3 10 C 28 5, 52 12, 74 7 S 108 6, 117 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.4"
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.55, delay: 0.7, ease: "easeOut" }}
        />
      </svg>
    </em>
  );
}

/** Quiet ticker — warm one-liners drifting by slowly, like overheard
 *  conversation. Pure cosmetics (aria-hidden); reuses © keys; freezes under
 *  reduced motion. */
function StingTicker() {
  const stings = [
    resolveCopy("onboard.ticker.1"),
    resolveCopy("onboard.ticker.2"),
    resolveCopy("onboard.ticker.3"),
    resolveCopy("onboard.ticker.4"),
    resolveCopy("onboard.ticker.5"),
  ].filter((s): s is string => Boolean(s));
  const line = stings.join("  ·  ");
  return (
    <div className="landing__ticker" aria-hidden="true">
      <div className="landing__ticker-track">
        <span>{line}</span>
        <span>{line}</span>
      </div>
    </div>
  );
}

export function LandingScreen() {
  const status = useStore(sessionStore, (s) => s.status);
  const lifecycle = useStore(sessionStore, (s) => s.lifecycle);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);

  // Lifecycle-aware (V1 integration fix): a hardcoded /deck here raced the
  // onLogin navigate() and dumped mid-onboarding/grace/banned sessions onto
  // the deck. Same routing rule as onLogin — routeForLifecycle is the truth.
  if (status === "authed") return <Navigate to={routeForLifecycle(lifecycle)} replace />;

  async function onLogin(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const pair = await api.post("/auth/login", {
        body: { email, password, device_fp: getDeviceFp() },
      });
      if (!pair?.access_token) throw new Error("no token");
      setSession(pair.access_token, pair.user ?? null, pair.lifecycle ?? null);
      navigate(routeForLifecycle(pair.lifecycle));
    } catch (err) {
      if (isApiError(err)) {
        if (err.status === 401) setError(resolveCopy("auth.bad_creds") ?? err.message);
        else if (err.status === 429) setError(resolveCopy("auth.throttled") ?? err.message);
        else setError(err.message); // 423 ban/grace payloads carry their own serious copy
      } else {
        setError(resolveCopy("errors.offline") ?? "Network error — try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onMagicLink() {
    if (!email) {
      setError("Enter your email first — the magic link needs a destination.");
      return;
    }
    setError(null);
    try {
      await api.post("/auth/magic-link", { body: { email } });
      setMagicSent(true);
    } catch (err) {
      if (isApiError(err, "RATE_LIMITED") || (isApiError(err) && err.status === 429)) {
        setError(resolveCopy("auth.throttled") ?? err.message);
      } else {
        setMagicSent(true); // 202 semantics: no account enumeration either way
      }
    }
  }

  function onGoogle() {
    // The PKCE code flow ships with the V1 auth-profiles vertical (it needs
    // the Google redirect round-trip). Until then the button answers with
    // the honest failure copy instead of pretending.
    toast(resolveCopy("auth.oauth_fail") ?? "Google sign-in unavailable.", "error");
  }

  return (
    <div className="landing">
      {/* the warm atmosphere: calm glow below, a whisper of grain on top */}
      <AmbientBackground />
      <GrainOverlay />

      <nav className="landing__nav">
        {/* wordmark only — Ticker below is the single hero heart (v6) */}
        <span className="frame__brand landing__sign">HUMBLE</span>
        <Link to="/safety" style={{ fontSize: "0.9rem" }}>
          Safety Center
        </Link>
      </nav>

      <div className="landing__hero">
        <section aria-label="What Humble is">
          {/* the host greets you first — mascot front & center (v5 §2) */}
          <motion.div
            className="landing__mascot"
            initial={{ opacity: 0, scale: 0.4, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ ...springs.jelly, delay: 0.12 }}
            aria-hidden="true"
          >
            <Mascot mood="idle" size={76} />
            <motion.span
              className="landing__bubble"
              initial={{ opacity: 0, scale: 0.7, x: -6 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ ...springs.bouncy, delay: 0.55 }}
            >
              {resolveCopy("mascot.landing_intro")}
            </motion.span>
          </motion.div>
          <motion.h1
            className="h-display landing__tagline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springs.gentle, delay: 0.05 }}
          >
            No such thing as a <Wrong /> swipe.
          </motion.h1>
          <motion.p
            className="landing__sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springs.gentle, delay: 0.18 }}
          >
            A fun, no-pressure way to meet people nearby.
          </motion.p>

          {/* the three warm panels — the friendly basics, above the fold (v6);
              the in-app twists stay a surprise ("You'll see") */}
          <motion.div
            className="landing__panels"
            variants={stagger}
            initial="initial"
            animate="enter"
          >
            <motion.div className="panel" variants={rise}>
              <span className="panel__step">IT'S A MATCH</span>
              <div className="panel__glyphs" aria-hidden="true">
                <span className="panel__match panel__glyph-l">
                  <PixelHeart size={16} /> →
                </span>
                <span className="panel__match panel__glyph-r">
                  ← <PixelHeart size={16} />
                </span>
                <span className="panel__result">
                  <span className="panel__eq">=</span>
                  <span className="panel__match panel__verdict">MATCH</span>
                </span>
              </div>
              <p className="panel__copy">{copy.onboard.panels[1]}</p>
            </motion.div>
            <motion.div className="panel" variants={rise}>
              <span className="panel__step">NO AWKWARD FIRST MOVE</span>
              <div className="panel__glyphs" aria-hidden="true">
                <span className="panel__match panel__glyph-l">
                  <PixelSparkle size={16} />
                </span>
                <span className="panel__result">
                  <span className="panel__match panel__verdict">NO RUSH</span>
                </span>
              </div>
              <p className="panel__copy">{copy.onboard.panels[2]}</p>
            </motion.div>
            {/* panel 3 keeps the bubblegum accent — a wink, not a warning */}
            <motion.div className="panel panel--reveal" variants={rise}>
              <span className="panel__step">LITTLE SURPRISES</span>
              <div className="panel__glyphs" aria-hidden="true">
                <span className="panel__crack">
                  <PixelHeartCracked size={18} className="panel__crack-icon" /> ?!
                </span>
              </div>
              <p className="panel__copy">{copy.onboard.panels[3]}</p>
            </motion.div>
          </motion.div>
        </section>

        <motion.section
          className="landing__auth"
          aria-label="Sign in or sign up"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.gentle, delay: 0.3 }}
        >
          <Card className="landing__authcard">
            <Button
              block
              size="lg"
              variant="primary"
              onClick={() => navigate("/signup/age")}
              style={{ marginBottom: 6 }}
            >
              Create account
            </Button>
            <p
              className="landing__authquip"
              style={{ textAlign: "center", margin: "0 0 4px" }}
            >
              Takes two minutes. Stay as long as you like.
            </p>

            <div className="landing__divider">or sign in</div>

            {magicSent ? (
              <p role="status">{resolveCopy("auth.verify_wait")}</p>
            ) : (
              <form onSubmit={onLogin}>
                <div className="field">
                  <label htmlFor="landing-email">Email</label>
                  <input
                    id="landing-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="landing-password">Password</label>
                  <input
                    id="landing-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {error && (
                  <motion.p
                    className="field__error"
                    role="alert"
                    initial={{ x: 0 }}
                    animate={{ x: [0, -7, 7, -4, 4, 0] }}
                    transition={{ duration: 0.4 }}
                  >
                    {error}
                  </motion.p>
                )}
                <Button type="submit" block variant="primary" loading={busy}>
                  Sign in
                </Button>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <Button block variant="ghost" onClick={() => void onMagicLink()}>
                    Email me a link
                  </Button>
                  <Button block variant="ghost" onClick={onGoogle}>
                    Google
                  </Button>
                </div>
              </form>
            )}
          </Card>
          <div className="landing__authlinks">
            <Link to="/signup/age">New here? Sign up</Link>
            <Link to="/safety">Safety Center</Link>
            <Link to="/legal/terms">Terms</Link>
            <Link to="/legal/privacy">Privacy</Link>
          </div>
        </motion.section>
      </div>

      <StingTicker />

      <footer className="landing__footer">
        <span className="landing__age">18+</span> · Humble is for adults only. ·{" "}
        <Link to="/legal/terms">Terms</Link> · <Link to="/legal/privacy">Privacy</Link> ·{" "}
        <Link to="/safety">Safety</Link>
      </footer>
    </div>
  );
}
